import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Empty,
  Form,
  Input,
  Modal,
  Radio,
  Row,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  CrownOutlined,
  FileSearchOutlined,
  OrderedListOutlined,
  StarOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { EvalTaskStickyHeader, type EvalStickyRule } from '../components/EvalTaskStickyHeader';
import { taskStatusColor, taskStatusLabel } from '../utils/taskStatusDisplay';
import { ANCHOR_COLORS } from '../utils/scoreColor';
import { competencyLevelLabel } from '@api-common/competency-level-names';
import type { RuleItem } from '../components/RulesPanel';

/**
 * 岗位胜任力 + 高潜识别 · 独立打分页（V1.2 合并版）。
 *
 * 路由：/leader/competency/:id  （:id = evaluateeId）
 *
 * 与「素质项评价（EvalFormPage）」交互对齐 —— 列表行点击直接跳到这张独立页面，
 * 不再用 Modal 浮窗。原因（用户 2026-04-30 22:45 提出）：
 *   - 浮窗在 PC 上空间受限，胜任力 + 红线 + 高潜识别（含 STAR 案例）一屏放不下
 *   - 与素质项评价的体验保持一致，URL 可分享、浏览器后退按钮可用
 *   - 手机端也走路由跳转，去掉之前的全屏 Modal hack
 *
 * 表单逻辑、提交校验、双提交（胜任力 → 高潜）、清空作答等均沿用原 LeaderCompetencyPage
 * Modal 内的实现，保持后端契约不变。
 */

interface Anchors {
  [score: string]: string;
}

interface Competency {
  code: string;
  name: string;
  weight: number;
  redLine?: boolean;
  anchors?: Anchors;
  desc?: string;
}

interface RedLineDef {
  code: string;
  desc: string;
}

interface RedLineSelection {
  code: string;
  hit: boolean;
  note?: string;
  occurredAt?: string;
}

interface CompetencyTaskDetail {
  batchId: number;
  batchPhase?: string;
  evaluateeId: number;
  evaluatee: {
    id: number;
    name: string;
    position?: string;
    deptPath?: string;
    avatar?: string;
  };
  modelVersion: number | null;
  competencies: Competency[];
  redLines: RedLineDef[];
  status: string;
  draft?: {
    scores?: Record<string, number>;
    comment?: string | null;
    redLines?: RedLineSelection[];
  };
}

interface HpFormDetail {
  batchId: number;
  batchName?: string;
  questions?: { code: string; name: string; desc?: string; anchors?: Record<string, string> }[];
  developPathOptions?: { code: string; name: string }[];
  thresholds?: { star_min?: number; per_dim_min?: number; avg_min?: number };
  starWindowStart?: string | null;
  starWindowEnd?: string | null;
  assessment?: {
    isHighPotential?: boolean;
    dimScores?: Record<string, number>;
    starEvidences?: any[];
    developPaths?: string[];
    developOther?: string | null;
    status?: string;
  } | null;
}

const REDLINE_CN: Record<string, string> = {
  V1: '红线一',
  V2: '红线二',
  V3: '红线三',
};
function redLineCn(code: string) {
  return REDLINE_CN[code] || code;
}

/**
 * 高潜识别四维评分锚定（A·成长性 / B·雄心抱负 / C·学习意愿 / D·价值观契合）。
 * 与原 LeaderCompetencyPage 完全一致，搬过来避免循环引用。
 */
const HP_DIM_ANCHORS: Record<'A' | 'B' | 'C' | 'D', Record<1 | 2 | 3 | 4 | 5, string>> = {
  A: {
    1: '过去 12 个月几乎无能力增长，多次任务结果与年初无差别；遇到新任务以「我没做过」为主要回应。',
    2: '偶有进步但需上级反复推动；接到新任务先问"以前怎么干"，独立学习意愿一般。',
    3: '在指定范围内能学会新东西；过去 12 个月有 1-2 次小幅能力突破，能稳定胜任本岗。',
    4: '主动跨出舒适区，过去 12 个月内明显跃迁（拿下过去做不到的项目 / 角色 / 难题）。',
    5: '跨级别跃迁，能独立啃下新领域并带动同事一起进步；用半年顶过去两年的成长。',
  },
  B: {
    1: '对自己未来无规划；不愿主动承担额外职责，"做完手头活"即下班心态。',
    2: '心里有想法但不主动推进；提及晋升 / 新机会被动等"被通知"，遇阻即放弃。',
    3: '有清晰的 1-3 年规划，能讲出现在差距和下一步打算；本岗范围内主动找事做。',
    4: '主动承担超出岗位的事来填差距；中长期规划与公司战略方向一致，敢提需要"赌一把"的目标。',
    5: '把个人发展与公司战略深度绑定，主动牵头新事，能影响并带动团队一起追更高目标。',
  },
  C: {
    1: '排斥反馈或反复犯同一错误；复盘走形式（"以后注意"），不输出可落地动作。',
    2: '接受反馈但落地慢，需上级反复提醒才动；学习多停留在被动培训。',
    3: '主动复盘项目，能落 1-2 个明确改进点；遇到不会的事会去查 / 去问。',
    4: '反馈即改，主动找资源（书 / 课 / 同行交流）补能力，并把所学带入工作改善流程。',
    5: '学习已是日常习惯，能把经验沉淀成方法论 / SOP 教他人，对团队整体能力有正向带动。',
  },
  D: {
    1: '行为多次与公司核心价值观相悖（如对客户敷衍、对队友推诿、对结果不负责）。',
    2: '大体不违反，但在压力 / 利益冲突下容易突破底线；价值观依赖外部约束。',
    3: '常规场景能做出与价值观一致的选择；遇困难场景需要提醒但能纠偏。',
    4: '在困难 / 冲突场景仍能做正确取舍；主动维护团队 / 公司氛围，是身边人的"标准答案"。',
    5: '是文化标杆，能影响他人；遇到价值观冲突主动正向干预，把价值观变成战斗力。',
  },
};

const COMPETENCY_RULES: RuleItem[] = [
  {
    title: '对照锚定逐档判断，不允许跳级',
    desc: '每个维度 5 档锚定描述代表能力的进阶。请逐档对照下属的实际工作表现，选择最贴近的档位。遵循"低档全部满足才能进入下一档"的原则——不要为了体面跳级给高分，也不要因为某次失误一刀切打低分。',
    color: '#722ed1',
    icon: <OrderedListOutlined />,
  },
  {
    title: '基于事实，全周期综合',
    desc: '评价依据是下属在本评价周期内的整体工作行为，而非单次事件或近期印象（避免"近因效应"）。重点参考你亲自观察到的关键工作场景，如项目交付、问题处理、跨部门协作等。如对某维度接触不足，请在评语中说明信息缺口，避免凭印象给分。',
    color: '#1677ff',
    icon: <FileSearchOutlined />,
  },
  {
    title: '红线判定，独立谨慎',
    desc: '三条红线为"一票否决项"，任一被勾选"已触发"将取消该下属本周期的晋升评优资格。红线与维度打分是两套独立机制——请基于客观事实独立判断；勾选"已触发"时务必附"具体事件 + 时间"备注，便于 HR 复核；未触发请保持"未触发"，不要为求"看起来公平"全部勾上。',
    color: '#ff4d4f',
    icon: <WarningOutlined />,
  },
  {
    title: '胜任力评完直接判高潜（V1.2）',
    desc: '在本表单尾部「高潜识别」一栏勾选「是 / 否」。是 → 填四维评分（成长性 / 雄心抱负 / 学习意愿 / 价值观契合，每档有锚定）+ STAR 案例 + 培养方向；否 → 不必填四维 / STAR / 培养方向，直接提交。两件事一并完成，不需要再去额外页面。',
    color: '#fa8c16',
    icon: <CrownOutlined />,
  },
];

const COMP_RULES_FOR_STICKY: EvalStickyRule[] = COMPETENCY_RULES.map((r) => ({
  title: r.title,
  detail: r.desc,
}));

/**
 * 兼容老接口：anchors 字段缺失时，回退解析 desc 串里的 "n分：xxx"。
 */
function parseAnchorFromDesc(desc: string | undefined, n: number): string | null {
  if (!desc) return null;
  for (const line of desc.split('\n')) {
    const m = line.match(/^(\d)分：(.*)$/);
    if (m && Number(m[1]) === n) return m[2];
  }
  return null;
}

export function LeaderCompFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const evaluateeId = useMemo(() => {
    const n = parseInt(id ?? '', 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [id]);

  const [openTimestamp, setOpenTimestamp] = useState<number>(0);
  const [form] = Form.useForm();

  const { data: detail, isLoading: detailLoading } = useQuery<CompetencyTaskDetail>({
    queryKey: ['my-competency-task', evaluateeId],
    queryFn: () => api.get(`/leader/competency-tasks/1/${evaluateeId}`),
    enabled: !!evaluateeId,
  });

  const { data: hpDetail, isLoading: hpLoading } = useQuery<HpFormDetail>({
    queryKey: ['my-hp-task', evaluateeId],
    queryFn: () => api.get(`/leader/hp-tasks/${evaluateeId}`),
    enabled: !!evaluateeId,
  });

  const { data: batchesAll = [] } = useQuery({
    queryKey: ['batches', 'leader-eval-sticky'],
    queryFn: () => api.get('/batches'),
    staleTime: 60_000,
  });
  const batchDeadlineById = useMemo(() => {
    const m = new Map<number, string>();
    for (const b of batchesAll as { id: number; endAt?: string | null }[]) {
      if (b?.id == null || !b.endAt) continue;
      try {
        m.set(Number(b.id), new Date(b.endAt).toLocaleDateString('zh-CN'));
      } catch {
        m.set(Number(b.id), '—');
      }
    }
    return m;
  }, [batchesAll]);

  /**
   * 数据返回后回填表单。逻辑与原 LeaderCompetencyPage 完全一致：
   *   - 先 resetFields 清空（避免不同下属间残留）
   *   - 仅当后端确实有 SUBMITTED 状态的胜任力 / 高潜评估时才回填
   *   - 红线 Switch 默认关；hit=true 必须 note + occurredAt
   */
  useEffect(() => {
    if (!detail || openTimestamp !== 0) return;
    setOpenTimestamp(Date.now());
    form.resetFields();

    const draftRedLines = detail.draft?.redLines ?? [];
    const competencySubmitted = detail.status === 'SUBMITTED';
    const initRedLines = (detail.redLines ?? []).map((r) => {
      const saved = draftRedLines.find((x) => x.code === r.code);
      return {
        code: r.code,
        hit: competencySubmitted ? !!saved?.hit : false,
        note: competencySubmitted ? (saved?.note ?? '') : '',
        occurredAt: competencySubmitted ? (saved?.occurredAt ?? '') : '',
      };
    });

    const a = hpDetail?.assessment;
    const hpSubmitted = a?.status === 'SUBMITTED';
    const allowedDevelopCodes = new Set(
      (hpDetail?.developPathOptions ?? []).map((x) => x.code),
    );
    const rawDp = Array.isArray(a?.developPaths) ? (a!.developPaths as string[]) : [];
    const developPathsFiltered = rawDp.filter((c) => allowedDevelopCodes.has(c));
    const hpScores = (a?.dimScores ?? {}) as Record<string, number>;
    const rawStars = Array.isArray(a?.starEvidences) ? (a!.starEvidences as any[]) : [];
    const stars =
      hpSubmitted && rawStars.length > 0
        ? rawStars.map((e) => ({
            situation: e.scene ?? '',
            task: e.task ?? '',
            action: e.action ?? '',
            result: e.result ?? '',
            occurredAt: typeof e.time === 'string' ? e.time.slice(0, 10) : '',
          }))
        : [{}];

    form.setFieldsValue({
      scores: competencySubmitted ? (detail.draft?.scores ?? {}) : {},
      comment: competencySubmitted ? (detail.draft?.comment ?? '') : '',
      redLines: initRedLines,
      isHighPotential: hpSubmitted ? !!a?.isHighPotential : false,
      hpScores: hpSubmitted
        ? { A: hpScores.A, B: hpScores.B, C: hpScores.C, D: hpScores.D }
        : { A: undefined, B: undefined, C: undefined, D: undefined },
      stars,
      developPaths: hpSubmitted ? developPathsFiltered : [],
      developOther: hpSubmitted ? (a?.developOther ?? '') : '',
    });
  }, [detail, hpDetail, form]);

  const submitMutation = useMutation({
    mutationFn: async (vals: any) => {
      const bid = detail?.batchId ?? hpDetail?.batchId;
      if (evaluateeId == null || bid == null) throw new Error('缺少批次信息，请返回列表后重试');
      const submitDuration = openTimestamp
        ? Math.floor((Date.now() - openTimestamp) / 1000)
        : undefined;
      await api.post(`/leader/competency-tasks/submit`, {
        scores: vals.scores ?? {},
        comment: vals.comment,
        redLines: vals.redLines,
        evaluateeId,
        batchId: bid,
        submitDuration,
      });

      const isHp = !!vals.isHighPotential;
      const starEvidences = (vals.stars ?? [])
        .filter((s: any) => (s?.situation ?? '').trim() || (s?.task ?? '').trim())
        .map((s: any) => ({
          scene: (s.situation ?? '').trim(),
          task: (s.task ?? '').trim(),
          action: (s.action ?? '').trim(),
          result: (s.result ?? '').trim(),
          time: s.occurredAt,
        }));

      try {
        const hpBody: Record<string, unknown> = {
          batchId: hpDetail?.batchId,
          evaluateeId,
          isHighPotential: isHp,
          submitDuration,
        };
        if (isHp) {
          hpBody.dimScores = {
            A: vals.hpScores?.A,
            B: vals.hpScores?.B,
            C: vals.hpScores?.C,
            D: vals.hpScores?.D,
          };
          hpBody.starEvidences = starEvidences;
          hpBody.developPaths = vals.developPaths ?? [];
          if ((vals.developPaths ?? []).includes('OTHER')) {
            hpBody.developOther = vals.developOther?.trim() || undefined;
          }
        }
        await api.post(`/leader/hp-tasks/submit`, hpBody);
      } catch (e: any) {
        const msg = e?.response?.data?.message ?? e?.message ?? '高潜识别提交失败';
        throw new Error(`胜任力已保存；高潜识别保存失败：${msg}`);
      }
    },
    onSuccess: () => {
      message.success('已提交（胜任力 + 高潜识别）');
      qc.invalidateQueries({ queryKey: ['my-competency-tasks'] });
      qc.invalidateQueries({ queryKey: ['my-hp-tasks-overview'] });
      navigate('/leader/competency');
    },
    onError: (e: any) => {
      const raw = e?.response?.data?.message ?? e?.message ?? '提交失败';
      const text = Array.isArray(raw) ? raw.join('；') : String(raw);
      message.error(text);
    },
  });

  const clearCompetencyMutation = useMutation({
    mutationFn: async () => {
      if (evaluateeId == null) return;
      const bid = detail?.batchId ?? hpDetail?.batchId;
      if (bid == null) throw new Error('缺少批次信息');
      await api.post('/leader/competency-tasks/clear-answers', {
        evaluateeId,
        batchId: bid,
      });
    },
    onSuccess: () => {
      message.success('已清空岗位胜任力与高潜作答，可重新填写');
      qc.invalidateQueries({ queryKey: ['my-competency-tasks'] });
      qc.invalidateQueries({ queryKey: ['my-competency-task', evaluateeId] });
      qc.invalidateQueries({ queryKey: ['my-hp-task', evaluateeId] });
      qc.invalidateQueries({ queryKey: ['my-hp-tasks-overview'] });
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message ?? e?.message ?? '清空失败');
    },
  });

  const onSubmit = async () => {
    if (!detail || detail.competencies.length === 0) {
      message.error(
        `未匹配到职位「${detail?.evaluatee.position || '-'}」的胜任力模型，请管理员先在「胜任力模型库」配置后再提交`,
      );
      return;
    }
    let vals: any;
    try {
      vals = await form.validateFields();
    } catch {
      message.warning('请完成所有作答再提交');
      return;
    }
    const duration = openTimestamp
      ? Math.floor((Date.now() - openTimestamp) / 1000)
      : 0;
    if (duration <= 20) {
      message.warning(`用时 ${duration} 秒，请认真作答`);
      return;
    }
    const isHp = !!vals.isHighPotential;
    const starMin = Number(hpDetail?.thresholds?.star_min ?? 2);
    const perDimMin = Number(hpDetail?.thresholds?.per_dim_min ?? 3);
    const avgMin = Number(hpDetail?.thresholds?.avg_min ?? 4);

    if (isHp) {
      const A = Number(vals.hpScores?.A);
      const B = Number(vals.hpScores?.B);
      const C = Number(vals.hpScores?.C);
      const D = Number(vals.hpScores?.D);
      if (!A || !B || !C || !D) {
        message.warning('请完成所有作答再提交');
        return;
      }
      const minDim = Math.min(A, B, C, D);
      if (minDim < perDimMin) {
        message.error(`识别为高潜要求每维 ≥ ${perDimMin} 分，当前最低维仅 ${minDim} 分`);
        return;
      }
      const avg = (A + B + C + D) / 4;
      if (avg < avgMin) {
        message.error(
          `识别高潜要求四维评价大于等于${avgMin}分，当前平均分为${avg.toFixed(2)}分，请重新评估是否为高潜人才`,
        );
        return;
      }
      const paths = vals.developPaths as string[] | undefined;
      if (!paths?.length) {
        message.warning('请完成所有作答再提交');
        return;
      }
      const filled = (vals.stars ?? []).filter(
        (s: any) =>
          (s?.situation ?? '').trim() &&
          (s?.task ?? '').trim() &&
          (s?.action ?? '').trim() &&
          (s?.result ?? '').trim() &&
          s?.occurredAt,
      );
      if (filled.length < starMin) {
        message.error(`识别为高潜时 STAR 须完整填写至少 ${starMin} 条`);
        return;
      }
      if (paths.includes('OTHER') && !(vals.developOther ?? '').trim()) {
        message.warning('请完成所有作答再提交');
        return;
      }
    }

    submitMutation.mutate(vals);
  };

  const developOptions =
    (hpDetail?.developPathOptions as { code: string; name: string }[] | undefined) ?? [];

  if (!evaluateeId) {
    return (
      <div>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/leader/competency')}>
          返回列表
        </Button>
        <Empty style={{ marginTop: 32 }} description="无效的下属 ID" />
      </div>
    );
  }

  if (detailLoading || hpLoading || !detail) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin tip="加载下属胜任力 + 高潜识别表单..." />
      </div>
    );
  }

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/leader/competency')}>
          返回列表
        </Button>
      </Space>

      <Card>
        <Form form={form} layout="vertical">
          <EvalTaskStickyHeader
            pageTitle="岗位胜任力 + 高潜识别"
            evaluatee={detail.evaluatee}
            roleTag={
              <Tag color="blue" style={{ marginLeft: 12 }}>
                直属上级
              </Tag>
            }
            batchName={hpDetail?.batchName}
            batchDeadlineLabel={batchDeadlineById.get(detail.batchId) ?? '—'}
            statusArea={
              <Space wrap size={8}>
                <Tag color={taskStatusColor(detail.status)}>
                  胜任力：{taskStatusLabel(detail.status)}
                </Tag>
                <Tag
                  color={
                    hpDetail?.assessment?.status
                      ? taskStatusColor(hpDetail.assessment.status)
                      : 'default'
                  }
                >
                  高潜：
                  {hpDetail?.assessment?.status
                    ? taskStatusLabel(hpDetail.assessment.status)
                    : '待完成'}
                </Tag>
              </Space>
            }
            rulesSectionHeading="胜任力与高潜 · 评价规则"
            rulesPopoverHeading="胜任力与高潜 · 评价规则（全文）"
            rules={COMP_RULES_FOR_STICKY}
          />

          <Alert
            type="info"
            showIcon
            message={
              <Space size="middle">
                <span>
                  模型版本：
                  <Typography.Text strong>v{detail.modelVersion ?? '-'}</Typography.Text>
                </span>
                <span>
                  维度数：
                  <Typography.Text strong>{detail.competencies.length}</Typography.Text>
                </span>
                <span>
                  红线数：
                  <Typography.Text strong>{detail.redLines.length}</Typography.Text>
                </span>
              </Space>
            }
            style={{ marginBottom: 16 }}
          />

          <Typography.Title level={5} style={{ marginBottom: 12 }}>
            一、岗位胜任力评价
          </Typography.Title>
          {detail.competencies.length === 0 ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={
                <>
                  未匹配到职位「
                  <Typography.Text strong>
                    {detail.evaluatee.position || '-'}
                  </Typography.Text>
                  」对应的胜任力模型
                </>
              }
              description={
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  <li>
                    请联系管理员（HR）在「岗位胜任力 · 模型库」补充该职位对应的胜任力模型
                  </li>
                  <li>
                    或由管理员在对应模型的「适用人员」里把
                    <Typography.Text strong> {detail.evaluatee.name} </Typography.Text>
                    勾选进去
                  </li>
                </ul>
              }
            />
          ) : (
            detail.competencies.map((c, idx) => (
              <Card
                key={c.code}
                size="small"
                style={{ marginBottom: 12 }}
                title={
                  <Space>
                    <Tag color="blue">维度 {idx + 1}</Tag>
                    <span style={{ fontWeight: 600 }}>{c.name}</span>
                    <Tag color="purple">权重 {c.weight}%</Tag>
                  </Space>
                }
                styles={{ body: { padding: 12 } }}
              >
                <Row gutter={[12, 8]} style={{ marginBottom: 12 }}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Col span={24} key={n}>
                      <div
                        style={{
                          display: 'flex',
                          gap: 10,
                          alignItems: 'flex-start',
                          padding: '4px 0',
                          borderBottom: n === 5 ? 'none' : '1px dashed #f0f0f0',
                        }}
                      >
                        <Tag
                          color={ANCHOR_COLORS[String(n)]}
                          style={{ minWidth: 92, textAlign: 'center', margin: 0 }}
                        >
                          {competencyLevelLabel(n)}
                        </Tag>
                        <span style={{ color: '#444', flex: 1 }}>
                          {c.anchors?.[String(n)] ||
                            parseAnchorFromDesc(c.desc, n) ||
                            '—'}
                        </span>
                      </div>
                    </Col>
                  ))}
                </Row>
                <Form.Item
                  name={['scores', c.code]}
                  label="本次得分"
                  rules={[{ required: true, message: '请选择 1-5 分' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Radio.Group
                    optionType="button"
                    buttonStyle="solid"
                    options={[1, 2, 3, 4, 5].map((n) => ({
                      label: competencyLevelLabel(n),
                      value: n,
                    }))}
                  />
                </Form.Item>
              </Card>
            ))
          )}

          <Form.Item label="评语（可选）" name="comment" style={{ marginTop: 16 }}>
            <Input.TextArea rows={3} placeholder="可补充加分项 / 待提升点的具体场景" />
          </Form.Item>

          <Typography.Title level={5} style={{ marginTop: 8, marginBottom: 8 }}>
            <WarningOutlined style={{ color: '#ff4d4f', marginRight: 6 }} />
            红线判定（任一触发将取消晋升评优资格）
          </Typography.Title>
          {detail.redLines.length === 0 ? (
            <Empty description="未配置红线" />
          ) : (
            <Form.List name="redLines">
              {(fields) => (
                <>
                  {fields.map((field, i) => {
                    const def = detail.redLines[i];
                    if (!def) return null;
                    return (
                      <Card
                        key={field.key}
                        size="small"
                        style={{ marginBottom: 8, borderLeft: '4px solid #ff4d4f' }}
                        styles={{ body: { padding: 12 } }}
                      >
                        <Space align="start" style={{ width: '100%' }} wrap>
                          <Tag color="red">{redLineCn(def.code)}</Tag>
                          <span style={{ color: '#444', flex: 1 }}>{def.desc}</span>
                        </Space>
                        <Row gutter={12} style={{ marginTop: 12 }} align="middle">
                          <Col flex="180px">
                            <Form.Item
                              {...field}
                              name={[field.name, 'hit']}
                              valuePropName="checked"
                              style={{ marginBottom: 0 }}
                              label={
                                <Typography.Text type="secondary">
                                  本次是否触发
                                </Typography.Text>
                              }
                            >
                              <Switch checkedChildren="已触发" unCheckedChildren="未触发" />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Form.Item
                          shouldUpdate={(prev, cur) => {
                            const a = prev?.redLines?.[field.name]?.hit;
                            const b = cur?.redLines?.[field.name]?.hit;
                            return a !== b;
                          }}
                          noStyle
                        >
                          {({ getFieldValue }) => {
                            const hit = !!getFieldValue(['redLines', field.name, 'hit']);
                            if (!hit) return null;
                            return (
                              <Row gutter={12} style={{ marginTop: 12 }}>
                                <Col xs={24} md={16}>
                                  <Form.Item
                                    {...field}
                                    name={[field.name, 'note']}
                                    style={{ marginBottom: 0 }}
                                    label={
                                      <Typography.Text strong>
                                        具体事件描述
                                        <Typography.Text type="danger" style={{ marginLeft: 4 }}>
                                          *
                                        </Typography.Text>
                                      </Typography.Text>
                                    }
                                    rules={[
                                      { required: true, message: '请说明触发该红线的具体事件' },
                                      {
                                        validator: (_r, v) =>
                                          (v ?? '').trim().length >= 10
                                            ? Promise.resolve()
                                            : Promise.reject(
                                                new Error('至少 10 个字描述事件经过'),
                                              ),
                                      },
                                    ]}
                                  >
                                    <Input.TextArea
                                      rows={3}
                                      placeholder="什么时间、什么场景、谁参与、做了 / 没做什么、造成了什么影响"
                                    />
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                  <Form.Item
                                    {...field}
                                    name={[field.name, 'occurredAt']}
                                    style={{ marginBottom: 0 }}
                                    label={
                                      <Typography.Text strong>
                                        发生时间
                                        <Typography.Text type="danger" style={{ marginLeft: 4 }}>
                                          *
                                        </Typography.Text>
                                      </Typography.Text>
                                    }
                                    rules={[
                                      { required: true, message: '请填写事件发生日期' },
                                    ]}
                                  >
                                    <Input type="date" />
                                  </Form.Item>
                                </Col>
                              </Row>
                            );
                          }}
                        </Form.Item>

                        <Form.Item {...field} name={[field.name, 'code']} hidden>
                          <Input />
                        </Form.Item>
                      </Card>
                    );
                  })}
                </>
              )}
            </Form.List>
          )}

          <Divider style={{ margin: '24px 0 16px', borderColor: '#fa8c16' }}>
            <Space>
              <CrownOutlined style={{ color: '#fa8c16' }} />
              <span style={{ fontWeight: 600, color: '#fa8c16' }}>
                二、高潜识别（V1.2 合并表单）
              </span>
            </Space>
          </Divider>

          <Alert
            type="warning"
            showIcon
            icon={<StarOutlined />}
            style={{ marginBottom: 12 }}
            message="高潜识别在评胜任力时一并完成"
            description={
              hpDetail?.starWindowStart && hpDetail?.starWindowEnd ? (
                <div style={{ marginTop: 4 }}>
                  <div style={{ marginBottom: 6 }}>
                    <Typography.Text strong>识别为高潜需同时满足：</Typography.Text>
                  </div>
                  <ul
                    style={{
                      margin: 0,
                      paddingLeft: 20,
                      lineHeight: 1.9,
                      color: '#5c4a1a',
                    }}
                  >
                    <li>
                      四维平均{' '}
                      <Typography.Text strong>
                        ≥ {(hpDetail.thresholds?.avg_min ?? 4).toFixed(1)}
                      </Typography.Text>{' '}
                      分
                    </li>
                    <li>
                      且四维各{' '}
                      <Typography.Text strong>
                        ≥ {hpDetail.thresholds?.per_dim_min ?? 3}
                      </Typography.Text>{' '}
                      分（任一维不足则整张废）
                    </li>
                    <li>
                      STAR 至少{' '}
                      <Typography.Text strong>
                        {hpDetail.thresholds?.star_min ?? 2}
                      </Typography.Text>{' '}
                      条，每条填 S/T/A/R 与日期
                    </li>
                    <li>
                      至少 <Typography.Text strong>1 条 STAR</Typography.Text> 落在{' '}
                      <Typography.Text code>
                        {String(hpDetail.starWindowStart).slice(0, 10)} ～{' '}
                        {String(hpDetail.starWindowEnd).slice(0, 10)}
                      </Typography.Text>
                    </li>
                    <li>培养方向至少选 1 项</li>
                  </ul>
                  <div
                    style={{
                      marginTop: 8,
                      paddingTop: 8,
                      borderTop: '1px dashed #ffd591',
                      color: '#7a5b1a',
                    }}
                  >
                    <Typography.Text type="secondary">
                      若不识别为高潜：四维 / STAR / 培养方向均无需填写，胜任力评分 + 红线判定完成后可直接提交。
                    </Typography.Text>
                  </div>
                </div>
              ) : (
                '请管理员先在「高潜识别」中配置本批次问卷与时间窗。'
              )
            }
          />

          <Form.Item
            label={
              <Space>
                <CrownOutlined style={{ color: '#fa8c16' }} />
                <span style={{ fontWeight: 600 }}>该下属是否为高潜人才</span>
              </Space>
            }
            name="isHighPotential"
            valuePropName="checked"
            extra="勾「是」需补充四维评分 + STAR 案例 + 培养方向；勾「否」可直接提交。"
          >
            <Switch checkedChildren="是 · 高潜" unCheckedChildren="否 · 非高潜" />
          </Form.Item>

          <Form.Item
            shouldUpdate={(prev, cur) => prev.isHighPotential !== cur.isHighPotential}
            noStyle
          >
            {({ getFieldValue }) =>
              getFieldValue('isHighPotential') ? (
                <>
                  <Typography.Title level={5} style={{ marginTop: 8, marginBottom: 4 }}>
                    四维评分（A·成长性 / B·雄心抱负 / C·学习意愿 / D·价值观契合）
                  </Typography.Title>
                  <Typography.Text
                    type="secondary"
                    style={{ display: 'block', marginBottom: 12, fontSize: 12 }}
                  >
                    每个维度对照下方 5 档锚定描述选择最贴近的档位。识别为高潜要求四维各 ≥{' '}
                    {hpDetail?.thresholds?.per_dim_min ?? 3} 分。
                  </Typography.Text>
                  {(hpDetail?.questions ?? []).map((q, idx) => {
                    const code = q.code as 'A' | 'B' | 'C' | 'D';
                    // 优先用后台配置的锚定（经 getFormDetail 下发），缺失时回退内置默认
                    const anchors = (q.anchors ?? HP_DIM_ANCHORS[code]) as Record<string, string>;
                    return (
                      <Card
                        key={q.code}
                        size="small"
                        style={{ marginBottom: 12 }}
                        title={
                          <Space>
                            <Tag color="gold">维度 {idx + 1}</Tag>
                            <span style={{ fontWeight: 600 }}>
                              {q.code}. {q.name}
                            </span>
                            {q.desc ? (
                              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {q.desc}
                              </Typography.Text>
                            ) : null}
                          </Space>
                        }
                        styles={{ body: { padding: 12 } }}
                      >
                        {anchors ? (
                          <Row gutter={[12, 8]} style={{ marginBottom: 12 }}>
                            {([1, 2, 3, 4, 5] as const).map((n) => (
                              <Col span={24} key={n}>
                                <div
                                  style={{
                                    display: 'flex',
                                    gap: 10,
                                    alignItems: 'flex-start',
                                    padding: '4px 0',
                                    borderBottom: n === 5 ? 'none' : '1px dashed #f0f0f0',
                                  }}
                                >
                                  <Tag
                                    color={ANCHOR_COLORS[String(n)]}
                                    style={{ minWidth: 44, textAlign: 'center', margin: 0 }}
                                  >
                                    {n} 分
                                  </Tag>
                                  <span style={{ color: '#444', flex: 1 }}>
                                    {anchors[String(n)]}
                                  </span>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        ) : null}
                        <Form.Item
                          name={['hpScores', q.code]}
                          label="本次得分"
                          rules={[{ required: true, message: '请选择 1-5 分' }]}
                          style={{ marginBottom: 0 }}
                        >
                          <Radio.Group
                            optionType="button"
                            buttonStyle="solid"
                            options={[1, 2, 3, 4, 5].map((n) => ({
                              label: `${n} 分`,
                              value: n,
                            }))}
                          />
                        </Form.Item>
                      </Card>
                    );
                  })}

                  <Typography.Title level={5} style={{ marginTop: 8, marginBottom: 4 }}>
                    STAR 案例（情境 / 任务 / 行动 / 结果）
                  </Typography.Title>
                  <Typography.Text
                    type="secondary"
                    style={{ display: 'block', marginBottom: 8, fontSize: 12 }}
                  >
                    用真实事件支撑「为什么是高潜」。每条都要 S/T/A/R 完整 + 发生时间。
                  </Typography.Text>
                  <Form.List name="stars" initialValue={[{}]}>
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map((field) => (
                          <Card
                            key={field.key}
                            type="inner"
                            size="small"
                            style={{ marginBottom: 12 }}
                          >
                            <Space direction="vertical" style={{ width: '100%' }}>
                              <Form.Item
                                label="S 情境"
                                name={[field.name, 'situation']}
                                rules={[{ required: true, message: '必填' }]}
                              >
                                <Input.TextArea rows={2} />
                              </Form.Item>
                              <Form.Item
                                label="T 任务"
                                name={[field.name, 'task']}
                                rules={[{ required: true, message: '必填' }]}
                              >
                                <Input.TextArea rows={2} />
                              </Form.Item>
                              <Form.Item
                                label="A 行动"
                                name={[field.name, 'action']}
                                rules={[{ required: true, message: '必填' }]}
                              >
                                <Input.TextArea rows={3} />
                              </Form.Item>
                              <Form.Item
                                label="R 结果"
                                name={[field.name, 'result']}
                                rules={[{ required: true, message: '必填' }]}
                              >
                                <Input.TextArea rows={2} />
                              </Form.Item>
                              <Form.Item
                                label="发生时间"
                                name={[field.name, 'occurredAt']}
                                rules={[{ required: true, message: '必填' }]}
                              >
                                <Input type="date" />
                              </Form.Item>
                              <Button danger type="text" onClick={() => remove(field.name)}>
                                删除案例
                              </Button>
                            </Space>
                          </Card>
                        ))}
                        <Button block onClick={() => add({})}>
                          + 添加 STAR 案例
                        </Button>
                      </>
                    )}
                  </Form.List>

                  <Typography.Title level={5} style={{ marginTop: 16, marginBottom: 4 }}>
                    下一阶段培养与发展的重点（至少 1 项）
                  </Typography.Title>
                  <Form.Item name="developPaths" rules={[{ required: false }]}>
                    <Checkbox.Group style={{ width: '100%' }}>
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        {developOptions.map((p) => (
                          <Checkbox key={p.code} value={p.code} style={{ width: '100%' }}>
                            {p.name}
                          </Checkbox>
                        ))}
                      </Space>
                    </Checkbox.Group>
                  </Form.Item>
                  <Form.Item shouldUpdate noStyle>
                    {({ getFieldValue: gf }) =>
                      (gf('developPaths') as string[] | undefined)?.includes('OTHER') ? (
                        <Form.Item
                          label="其他说明"
                          name="developOther"
                          rules={[{ required: true, message: '请填写' }]}
                        >
                          <Input.TextArea rows={2} placeholder="勾选「其他」时必填" />
                        </Form.Item>
                      ) : null
                    }
                  </Form.Item>
                </>
              ) : (
                <Alert
                  type="success"
                  showIcon
                  style={{ marginTop: 8 }}
                  message="非高潜：四维 / STAR / 培养方向均无需填写"
                  description="完成上方胜任力评分 + 红线判定后，点击「提交」即可。"
                />
              )
            }
          </Form.Item>
        </Form>

        <div
          style={{
            marginTop: 24,
            paddingTop: 16,
            borderTop: '1px solid #f0f0f0',
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <Space wrap>
            <Button onClick={() => navigate('/leader/competency')}>取消</Button>
            <Button
              type="primary"
              loading={submitMutation.isPending}
              disabled={detail.competencies.length === 0}
              onClick={onSubmit}
            >
              {detail.status === 'SUBMITTED' ? '重新提交' : '提交'}
            </Button>
          </Space>

          {detail.batchPhase === 'ONGOING' && (
            <Button
              danger
              type="default"
              loading={clearCompetencyMutation.isPending}
              onClick={() => {
                Modal.confirm({
                  title: '清空岗位胜任力与高潜作答？',
                  content:
                    '将删除已填维度分、红线、高潜四维/STAR/培养方向等，任务回到待完成。仅当前批次进行中可用。',
                  okText: '确认清空',
                  okButtonProps: { danger: true },
                  onOk: () => clearCompetencyMutation.mutate(),
                });
              }}
            >
              清空作答
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
